// Catálogo dos nós do Flow Builder.
//
// Um nó era descrito em quatro lugares: `nodeTypes` (qual componente desenha),
// `NODE_DEFAULTS` (com que campos ele nasce), `NODE_LABELS` (como se chama) e
// a `PALETTE` (onde aparece para ser arrastado) — os três primeiros em
// `nodes.jsx`, o último em `index.jsx`. Esquecer um deles não quebra o build:
// o nó só aparece sem nome, abre vazio, ou some da paleta e vira uma função
// que existe no backend e ninguém consegue usar.
//
// Aqui cada nó é UMA entrada, e os quatro mapas são derivados dela. Para
// acrescentar um nó: escreva o componente, some a entrada e registre o `case`
// correspondente em `backend/src/services/FlowService/ExecuteFlowService.ts`.
//
// `paleta: false` marca o que existe mas não é oferecido: o gatilho (criado
// junto com o fluxo) e o `httpRequest`, substituído pelo nó de Integração com
// `service: "http"` — continua desenhando e executando os fluxos antigos.

import {
  Ban,
  CircleCheck,
  Clock,
  Columns3,
  CornerUpLeft,
  Flag,
  GitBranch,
  HelpCircle,
  Image,
  Layers,
  List as ListIcon,
  ListOrdered,
  MessageSquare,
  MousePointerClick,
  Plug,
  Shuffle,
  Sparkles,
  StickyNote,
  Tag,
  UserCheck,
  Users,
  Variable,
  Zap,
  Globe,
} from "lucide-react";

import {
  AddTagNode,
  AiNode,
  ArchiveNode,
  AssignUserNode,
  BlockNode,
  BoardNode,
  ButtonsNode,
  ConditionNode,
  ContentNode,
  DelayNode,
  EndNode,
  HttpRequestNode,
  IntegrationNode,
  JumpNode,
  ListNode,
  MarkReadNode,
  MediaNode,
  MenuNode,
  NoteNode,
  QuestionNode,
  RandomizerNode,
  SendMessageNode,
  SetVariableNode,
  TicketStatusNode,
  TransferQueueNode,
  TriggerNode,
  TypingNode,
} from "./nodes";

// Os grupos da paleta, na ordem em que aparecem. Agrupada por intenção:
// primeiro o que fala com o cliente, depois o que decide o caminho, e por fim
// o que mexe no atendimento. Uma lista corrida de vinte e cinco itens
// obrigaria a ler tudo para achar um.
export const GRUPOS_DA_PALETA = [
  { id: "fala", titulo: "O que o robô diz", acento: "fala" },
  { id: "pergunta", titulo: "O que o robô pergunta", acento: "pergunta" },
  { id: "caminho", titulo: "Por onde a conversa segue", acento: "caminho" },
  { id: "atendimento", titulo: "Atendimento", acento: "atendimento" },
  { id: "conversa", titulo: "O que o robô faz na conversa", acento: "atendimento" },
  { id: "integracoes", titulo: "Integrações", acento: "integracao" },
];

export const NODE_CATALOG = [
  // ---- gatilho: nasce com o fluxo, não é oferecido na paleta -------------
  {
    type: "trigger",
    label: "Gatilho",
    icon: Zap,
    component: TriggerNode,
    paleta: false,
    defaults: {
      triggerType: "manual",
      keywords: [],
      matchMode: "contains",
      caseSensitive: false,
      delayMin: 0,
      delayMax: 0,
      quoteReply: false,
      escopo: "individual",
      respostaPrivada: false,
      naoSeAtendida: false,
      somenteComMidia: false,
      diasDaSemana: [],
      maxPorAtendimento: 0,
    },
  },

  // ---- o que o robô diz ---------------------------------------------------
  {
    type: "content",
    label: "Conteúdo",
    icon: Layers,
    component: ContentNode,
    grupo: "fala",
    defaults: { items: [{ kind: "text", value: "" }] },
  },
  {
    type: "media",
    label: "Enviar mídia",
    icon: Image,
    component: MediaNode,
    grupo: "fala",
    defaults: { mediaType: "image", url: "", caption: "" },
  },
  {
    type: "sendMessage",
    // Rótulo da paleta é mais específico que o do nó: ali é preciso deixar
    // claro que a mensagem sai para OUTRO número, não para quem conversa.
    label: "Enviar mensagem",
    labelNaPaleta: "Avisar outro número",
    icon: MessageSquare,
    component: SendMessageNode,
    grupo: "fala",
    defaults: { whatsappId: "", number: "", body: "" },
  },
  {
    type: "ai",
    label: "Inteligência Artificial",
    icon: Sparkles,
    component: AiNode,
    grupo: "fala",
    defaults: { prompt: "" },
  },

  // ---- o que o robô pergunta ---------------------------------------------
  {
    type: "menu",
    label: "Menu",
    icon: ListOrdered,
    component: MenuNode,
    grupo: "pergunta",
    defaults: {
      message: "",
      options: [{ number: "1", label: "" }],
      invalidMessage: "Opção inválida. Tente de novo.",
      maxAttempts: 3,
    },
  },
  {
    type: "buttons",
    label: "Botões",
    icon: MousePointerClick,
    component: ButtonsNode,
    grupo: "pergunta",
    defaults: {
      message: "",
      footer: "",
      options: [{ number: "1", label: "" }],
      invalidMessage: "Opção inválida. Tente de novo.",
      maxAttempts: 3,
    },
  },
  {
    type: "list",
    label: "Lista",
    icon: ListIcon,
    component: ListNode,
    grupo: "pergunta",
    defaults: {
      message: "",
      footer: "",
      listTitle: "",
      buttonText: "Ver opções",
      options: [{ number: "1", label: "", description: "" }],
      invalidMessage: "Opção inválida. Tente de novo.",
      maxAttempts: 3,
    },
  },
  {
    type: "question",
    label: "Pergunta",
    icon: HelpCircle,
    component: QuestionNode,
    grupo: "pergunta",
    defaults: {
      message: "",
      variable: "",
      answerType: "texto",
      invalidMessage: "",
    },
  },

  // ---- por onde a conversa segue -----------------------------------------
  {
    type: "condition",
    label: "Condição",
    icon: GitBranch,
    component: ConditionNode,
    grupo: "caminho",
    defaults: { field: "", operator: "equals", value: "" },
  },
  {
    type: "randomizer",
    label: "Sorteio",
    icon: Shuffle,
    component: RandomizerNode,
    grupo: "caminho",
    defaults: { percent: 50 },
  },
  {
    type: "delay",
    label: "Atraso",
    icon: Clock,
    component: DelayNode,
    grupo: "caminho",
    defaults: { seconds: 5 },
  },
  {
    type: "setVariable",
    label: "Definir variável",
    icon: Variable,
    component: SetVariableNode,
    grupo: "caminho",
    defaults: { variable: "", value: "" },
  },
  {
    type: "jump",
    label: "Pular para",
    icon: CornerUpLeft,
    component: JumpNode,
    grupo: "caminho",
    defaults: { targetNodeId: "", targetLabel: "" },
  },

  // ---- atendimento --------------------------------------------------------
  {
    type: "transferQueue",
    label: "Transferir para fila",
    icon: Users,
    component: TransferQueueNode,
    grupo: "atendimento",
    defaults: { queueId: "", queueName: "" },
  },
  {
    type: "assignUser",
    label: "Atribuir atendente",
    icon: UserCheck,
    component: AssignUserNode,
    grupo: "atendimento",
    defaults: { userId: "", userName: "" },
  },
  {
    type: "ticketStatus",
    label: "Situação do atendimento",
    labelNaPaleta: "Situação",
    icon: CircleCheck,
    component: TicketStatusNode,
    grupo: "atendimento",
    defaults: { status: "pending" },
  },
  {
    type: "addTag",
    label: "Aplicar etiqueta",
    icon: Tag,
    component: AddTagNode,
    grupo: "atendimento",
    defaults: {
      tagId: "",
      tagName: "",
      acao: "adicionar",
      criarSeNaoExistir: false,
    },
  },
  {
    type: "note",
    label: "Nota interna",
    icon: StickyNote,
    component: NoteNode,
    grupo: "atendimento",
    defaults: { note: "" },
  },
  {
    type: "board",
    label: "Mover no quadro",
    icon: Columns3,
    component: BoardNode,
    grupo: "atendimento",
    defaults: { laneId: "", laneName: "" },
  },
  {
    type: "end",
    label: "Fim",
    icon: Flag,
    component: EndNode,
    grupo: "atendimento",
    defaults: { message: "", closeTicket: false },
  },

  // ---- o que o robô faz na conversa --------------------------------------
  {
    type: "typing",
    label: "Digitando",
    icon: Clock,
    component: TypingNode,
    grupo: "conversa",
    defaults: { seconds: 3 },
  },
  {
    type: "markRead",
    label: "Marcar como lida",
    icon: CircleCheck,
    component: MarkReadNode,
    grupo: "conversa",
    defaults: {},
  },
  {
    type: "archive",
    label: "Arquivar conversa",
    labelNaPaleta: "Arquivar",
    icon: Layers,
    component: ArchiveNode,
    grupo: "conversa",
    defaults: { desfazer: false },
  },
  {
    type: "block",
    label: "Bloquear contato",
    labelNaPaleta: "Bloquear",
    icon: Ban,
    component: BlockNode,
    grupo: "conversa",
    defaults: { desfazer: false },
  },

  // ---- integrações --------------------------------------------------------
  {
    type: "integration",
    label: "Integração",
    icon: Plug,
    component: IntegrationNode,
    grupo: "integracoes",
    defaults: {
      service: "http",
      method: "GET",
      url: "",
      headers: [],
      body: "",
      operation: "append",
      spreadsheetId: "",
      calendarId: "primary",
      range: "A1",
      token: "",
      values: "",
      summary: "",
      location: "",
      startDateTime: "",
      endDateTime: "",
      timeMin: "",
      timeMax: "",
      maxResults: "20",
      timezone: "America/Sao_Paulo",
      value: "",
      description: "",
      billingType: "PIX",
      dueDate: "",
      chargeId: "",
    },
  },
  {
    // Substituído pelo nó de Integração com `service: "http"`. Continua no
    // catálogo para desenhar e executar os fluxos que já o usam.
    type: "httpRequest",
    label: "Requisição HTTP",
    icon: Globe,
    component: HttpRequestNode,
    paleta: false,
    defaults: { method: "GET", url: "", body: "" },
  },
];

const porTipo = new Map(NODE_CATALOG.map((no) => [no.type, no]));

export const acharNo = (type) => porTipo.get(type);

// ---- os quatro mapas, agora derivados ------------------------------------

export const nodeTypes = Object.fromEntries(
  NODE_CATALOG.map((no) => [no.type, no.component]),
);

export const NODE_DEFAULTS = Object.fromEntries(
  NODE_CATALOG.map((no) => [no.type, no.defaults]),
);

export const NODE_LABELS = Object.fromEntries(
  NODE_CATALOG.map((no) => [no.type, no.label]),
);

// A paleta: só o que é oferecido, agrupado na ordem declarada acima. Grupo sem
// nenhum nó não aparece.
export const PALETTE = GRUPOS_DA_PALETA.map((grupo) => ({
  grupo: grupo.titulo,
  acento: grupo.acento,
  itens: NODE_CATALOG.filter(
    (no) => no.paleta !== false && no.grupo === grupo.id,
  ).map((no) => ({
    type: no.type,
    label: no.labelNaPaleta ?? no.label,
    icon: no.icon,
  })),
})).filter((grupo) => grupo.itens.length > 0);
